import {
  requestSessionOpening,
  dropSessionOpening,
  signUpForSessionOpening,
  signUpForClassOpenings,
  withdrawOpeningSignup,
} from "../../api/apiClient";

/**
 * What an instructor can do with a slot in their scheduling feed:
 *   drop     - it is theirs (filled)
 *   request  - first-come opening: claims it immediately
 *   signup   - unlimited-sign-up opening: becomes Pending Approval
 *   withdraw - they have a pending sign-up
 *   null     - nothing (their sign-up was not approved, or the slot went to someone else)
 */
export function slotActionKind(opening) {
  if (opening.status === "FILLED") {
    // Someone else was chosen: my own pending sign-up can still be cleared, but there is nothing to drop.
    if (opening.assignedToMe === false) return opening.mySignupStatus === "PENDING" ? "withdraw" : null;
    return "drop";
  }
  if (opening.signupMode === "APPROVAL") {
    if (opening.mySignupStatus === "PENDING") return "withdraw";
    if (opening.mySignupStatus === "UNAPPROVED") return null;
    return "signup";
  }
  return "request";
}

/** Optimistic change to a feed row while the request is in flight. */
export function optimisticPatch(kind, row) {
  switch (kind) {
    case "request": return { status: "FILLED" };
    case "drop": return { status: "OPEN", assignedInstructorId: null, mySignupStatus: null };
    case "signup": return { mySignupStatus: "PENDING" };
    case "withdraw": return { mySignupStatus: null };
    default: return row;
  }
}

export function runSlotAction(kind, opening, tenantId) {
  const { sessionId, id } = opening;
  switch (kind) {
    case "request": return requestSessionOpening(sessionId, id, tenantId);
    case "drop": return dropSessionOpening(sessionId, id, tenantId);
    case "signup": return signUpForSessionOpening(sessionId, id, tenantId);
    case "withdraw": return withdrawOpeningSignup(sessionId, id, tenantId);
    default: return Promise.reject(new Error(`Unknown slot action: ${kind}`));
  }
}

/**
 * Signs up for several openings in one call per class (one email to the coordinators), whatever the class mix.
 * Always resolves with one result per opening: { openingId, status: CREATED | REFUSED | NOT_FOUND, reason }.
 * A class whose call fails outright (network, 403) reports every one of its openings as REFUSED with that reason.
 */
export async function runSignupBatch(openings, tenantId) {
  const byClass = new Map();
  openings.forEach((o) => {
    if (!byClass.has(o.classId)) byClass.set(o.classId, []);
    byClass.get(o.classId).push(o);
  });
  const results = [];
  for (const [classId, list] of byClass) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await signUpForClassOpenings(classId, list.map((o) => o.id), tenantId);
      results.push(...(data?.results ?? []));
    } catch (e) {
      const reason = friendlySlotError(e, "signup");
      results.push(...list.map((o) => ({ openingId: o.id, status: "REFUSED", reason })));
    }
  }
  return results;
}

export const SUCCESS_MESSAGE = {
  request: "Slot requested — you're now assigned.",
  drop: "Slot dropped — it's open again.",
  signup: "Signed up — pending coordinator approval.",
  withdraw: "Sign-up withdrawn.",
};

const VERB = { request: "request", drop: "drop", signup: "sign up for", withdraw: "withdraw from" };

export function friendlySlotError(e, kind) {
  const status = e?.response?.status;
  const msg = e?.response?.data?.message || "";
  // 409 means different things: lost the race, a schedule conflict, a completed class. Only the race is generic.
  if (kind === "request" && status === 409 && (!msg || /already filled/i.test(msg))) return "Someone just claimed this slot.";
  if ((kind === "request" || kind === "signup") && status === 403) return "You're not qualified for this role.";
  if (kind === "drop" && status === 409 && (!msg || /within 2 days/i.test(msg))) return "Too close to the session date to drop (2-day notice required).";
  if (kind === "signup" && status === 409 && !msg) return "This slot is no longer taking sign-ups.";
  return msg || `Could not ${VERB[kind] || kind} this slot.`;
}
